// Initialize i18n before React renders
import './i18n';

import React from 'react';
import { Provider } from 'react-redux';
import { CookiesProvider } from 'react-cookie';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as ChakraProvider } from '@chakra-primitives/provider';
import { Toaster } from '@chakra-primitives/toaster';
import store from './store';
import Root from './components/root';
import queryClient from './query-client';
import { initializeDatadogRum } from './helpers/datadog-rum';
import loadPendoAgent from './helpers/pendo/load-pendo-agent';

import './styles/main.less';

try {
  if (!navigator) {
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator
    throw new Error('The navigator interface was not found');
  }
  if (!navigator.serviceWorker) {
    /**
     * This situation is possible given certain browsers may not yet have
     * service worker supported. See browser compatibility in the linked
     * documentation
     * https://developer.mozilla.org/en-US/docs/Web/API/Navigator/serviceWorker
     */
    throw new Error('The "serviceWorker" property of the navigator interface was not found');
  }
  if (!navigator.serviceWorker.register) {
    // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/register
    throw new Error('The "register" function was not found on the service worker instance');
  }
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js').then(registration => {
        console.log('SW registered: ', registration);
      }).catch(registrationError => {
        console.log('SW registration failed: ', registrationError);
      });
    });
  }
} catch (e) {
  console.error('There was an issue trying to register the service worker');
  console.error(e);
}

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <CookiesProvider>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ChakraProvider>
          <Root />
          <Toaster />
        </ChakraProvider>
      </Provider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </CookiesProvider>,
);

initializeDatadogRum();
loadPendoAgent();
